import React, { useState, useEffect } from "react";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, KeyRound, CheckCircle2, XCircle, Clock, Ban, Shield, RefreshCw } from "lucide-react";

const STATUS_LABEL = { pending: "معلّق", approved: "موافَق عليه", denied: "مرفوض", expired: "منتهي" };
const STATUS_COLOR = { pending: "bg-amber-100 text-amber-700", approved: "bg-emerald-100 text-emerald-700", denied: "bg-red-100 text-red-700", expired: "bg-slate-100 text-slate-500" };

export default function SupportAccessRequests() {
  const { user } = useAuth();
  const isSupport = user?.role === "support";
  const [items, setItems] = useState([]);
  const [grants, setGrants] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dlg, setDlg] = useState(false);
  const [respondDlg, setRespondDlg] = useState(null); // {req, approve, duration_hours, reason}
  const [form, setForm] = useState({ company_id: "", message: "", duration_hours: 24 });

  const load = async () => {
    setLoading(true);
    try {
      const requests = api.get("/support/access-requests");
      const activeGrants = api.get("/support/grants?active_only=true");
      let compsCall;
      if (isSupport) {
        // Support: list *all* companies from a special endpoint that shows what could be requested.
        // For now use companies endpoint (which for support returns only granted ones);
        // list of all requestable companies comes from a separate call.
        compsCall = api.get("/support/companies-all").catch(() => ({ data: [] }));
      } else {
        compsCall = Promise.resolve({ data: [] });
      }
      const [r, g, c] = await Promise.all([requests, activeGrants, compsCall]);
      setItems(r.data); setGrants(g.data); setCompanies(c.data);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const submitRequest = async () => {
    if (!form.company_id) return toast.error("اختر شركة");
    try {
      await api.post("/support/access-request", form);
      toast.success("تم إرسال الطلب - بانتظار موافقة مدير الشركة");
      setDlg(false); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const respond = async () => {
    const r = respondDlg;
    try {
      await api.post(`/support/access-requests/${r.req.id}/respond`, {
        approve: r.approve,
        duration_hours: r.duration_hours,
        reason: r.reason,
      });
      toast.success(r.approve ? "تمت الموافقة - تصريح دخول نشط الآن" : "تم رفض الطلب");
      setRespondDlg(null); load();
    } catch (e) { toast.error(formatErr(e)); }
  };

  const revokeGrant = async (g) => {
    if (!window.confirm(`إلغاء تصريح "${g.support_user_name}" فوراً؟`)) return;
    try { await api.post(`/support/grants/${g.id}/revoke`); toast.success("تم الإلغاء"); load(); }
    catch (e) { toast.error(formatErr(e)); }
  };

  return (
    <div className="space-y-6" data-testid="support-access-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h1 className="font-heading font-bold text-2xl flex items-center gap-2"><KeyRound className="w-6 h-6 text-primary" /> {isSupport ? "طلبات وتصاريح الدخول" : "طلبات دخول الدعم لشركاتك"}</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {isSupport ? "اطلب صلاحية دخول مؤقتة لأي شركة عميلة لتقديم المساعدة" : "وافق أو ارفض طلبات دخول موظفي الدعم لشركاتك"}
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={load}><RefreshCw className="w-4 h-4 me-1" /> تحديث</Button>
          {isSupport && <Button onClick={() => setDlg(true)} className="gap-2" data-testid="new-request-btn"><Plus className="w-4 h-4" /> طلب جديد</Button>}
        </div>
      </div>

      {/* Active Grants */}
      <div>
        <h2 className="font-heading font-bold text-lg mb-2 flex items-center gap-2"><Shield className="w-5 h-5 text-emerald-600" /> التصاريح النشطة ({grants.length})</h2>
        {grants.length === 0 ? (
          <div className="text-center py-6 border-2 border-dashed rounded-lg text-muted-foreground text-sm">لا توجد تصاريح نشطة</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
            {grants.map(g => (
              <div key={g.id} className="border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-3" data-testid={`grant-${g.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div>
                    <div className="font-bold text-sm">{isSupport ? g.company_name : g.support_user_name}</div>
                    <div className="text-xs text-muted-foreground">{isSupport ? `مُنح بواسطة: ${g.granted_by_name || "—"}` : `شركة: ${g.company_name}`}</div>
                  </div>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                </div>
                <div className="text-xs text-muted-foreground border-t pt-2 space-y-0.5">
                  <div>ينتهي: <b className="text-foreground">{new Date(g.expires_at).toLocaleString("ar-EG")}</b></div>
                  <div>المدة: <b className="text-foreground">{g.duration_hours} ساعة</b></div>
                </div>
                <Button size="sm" variant="outline" className="w-full mt-2 h-7 text-xs text-destructive border-red-200 hover:bg-red-50" onClick={() => revokeGrant(g)}>
                  <Ban className="w-3 h-3 me-1" /> إلغاء فوراً
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Requests list */}
      <div>
        <h2 className="font-heading font-bold text-lg mb-2">سجل الطلبات</h2>
        {loading ? <div className="text-center text-muted-foreground py-6">جاري التحميل...</div> :
         items.length === 0 ? (
          <div className="text-center py-10 border-2 border-dashed rounded-lg text-muted-foreground">لا توجد طلبات</div>
         ) : (
          <div className="border rounded-lg overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 border-b">
                <tr>
                  <th className="text-start p-3">{isSupport ? "الشركة" : "موظف الدعم"}</th>
                  <th className="text-start p-3">الرسالة</th>
                  <th className="text-center p-3">المدة</th>
                  <th className="text-center p-3">الحالة</th>
                  <th className="text-start p-3">التاريخ</th>
                  <th className="text-center p-3">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {items.map(r => (
                  <tr key={r.id} className="border-b hover:bg-muted/30" data-testid={`req-${r.id}`}>
                    <td className="p-3 font-medium">{isSupport ? r.company_name : `${r.support_user_name} · ${r.support_user_email}`}</td>
                    <td className="p-3 text-muted-foreground text-xs max-w-xs truncate">{r.message || "—"}</td>
                    <td className="p-3 text-center font-mono text-xs">{r.duration_hours}h</td>
                    <td className="p-3 text-center"><span className={`px-2 py-0.5 rounded text-xs ${STATUS_COLOR[r.status]}`}>{STATUS_LABEL[r.status]}</span></td>
                    <td className="p-3 text-xs text-muted-foreground">{new Date(r.requested_at).toLocaleString("ar-EG")}</td>
                    <td className="p-3 text-center">
                      {!isSupport && r.status === "pending" ? (
                        <div className="flex gap-1 justify-center">
                          <Button size="sm" variant="outline" className="h-7 text-xs text-emerald-700 border-emerald-200 hover:bg-emerald-50" onClick={() => setRespondDlg({ req: r, approve: true, duration_hours: r.duration_hours, reason: "" })} data-testid={`approve-${r.id}`}>
                            <CheckCircle2 className="w-3.5 h-3.5 me-1" /> موافقة
                          </Button>
                          <Button size="sm" variant="outline" className="h-7 text-xs text-red-700 border-red-200 hover:bg-red-50" onClick={() => setRespondDlg({ req: r, approve: false, duration_hours: r.duration_hours, reason: "" })} data-testid={`deny-${r.id}`}>
                            <XCircle className="w-3.5 h-3.5 me-1" /> رفض
                          </Button>
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground">
                          {r.status === "pending" ? <Clock className="w-3.5 h-3.5 inline" /> : r.responded_by_name || "—"}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
         )}
      </div>

      {/* New Request (support only) */}
      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent data-testid="new-request-dialog">
          <DialogHeader><DialogTitle>طلب دخول لشركة عميلة</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div>
              <Label>الشركة</Label>
              <Select value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="req-company"><SelectValue placeholder="اختر شركة" /></SelectTrigger>
                <SelectContent>{companies.map(c => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
              {companies.length === 0 && <p className="text-xs text-amber-700 mt-1">لا توجد شركات متاحة - اطلب من الإدارة تفعيل الوصول</p>}
            </div>
            <div>
              <Label>المدة المطلوبة (بالساعات)</Label>
              <Input type="number" min={1} max={720} value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: +e.target.value || 24 })} data-testid="req-duration" />
            </div>
            <div>
              <Label>سبب الطلب / رسالة للعميل</Label>
              <Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={3} placeholder="مثال: مراجعة قيود شهر يناير..." />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlg(false)}>إلغاء</Button>
            <Button onClick={submitRequest} data-testid="submit-req-btn">إرسال الطلب</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Respond (manager only) */}
      <Dialog open={!!respondDlg} onOpenChange={(o) => !o && setRespondDlg(null)}>
        <DialogContent data-testid="respond-dialog">
          <DialogHeader><DialogTitle>{respondDlg?.approve ? "الموافقة على طلب الدخول" : "رفض طلب الدخول"}</DialogTitle></DialogHeader>
          {respondDlg && (
            <div className="space-y-3">
              <div className="p-3 rounded bg-muted/30 border text-sm">
                <div><b>{respondDlg.req.support_user_name}</b> ({respondDlg.req.support_user_email})</div>
                <div className="text-xs text-muted-foreground mt-1">يطلب الدخول إلى: <b>{respondDlg.req.company_name}</b></div>
                {respondDlg.req.message && <div className="mt-2 text-xs bg-white p-2 rounded">💬 {respondDlg.req.message}</div>}
              </div>
              {respondDlg.approve && (
                <div>
                  <Label>مدة التصريح (ساعات)</Label>
                  <Input type="number" min={1} max={720} value={respondDlg.duration_hours} onChange={(e) => setRespondDlg({ ...respondDlg, duration_hours: +e.target.value || 24 })} data-testid="respond-duration" />
                  <p className="text-xs text-muted-foreground mt-1">يمكنك الإلغاء الفوري في أي وقت</p>
                </div>
              )}
              <div>
                <Label>ملاحظة (اختياري)</Label>
                <Textarea value={respondDlg.reason} onChange={(e) => setRespondDlg({ ...respondDlg, reason: e.target.value })} rows={2} />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRespondDlg(null)}>إلغاء</Button>
            <Button onClick={respond} className={respondDlg?.approve ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"} data-testid="confirm-respond-btn">
              {respondDlg?.approve ? "موافقة" : "رفض"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
